import { motion } from 'framer-motion';
import { X, Save, Sparkles, Loader, Brain, Image as ImageIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import {
  blogApi,
  BlogPost,
  BlogTag,
  BlogCategory,
  CreateBlogPostData,
  UpdateBlogPostData,
  BlogGenerationOptions,
  ContentSuggestion,
} from '../services/api';
import ContentSuggestionsPanel from './ContentSuggestionsPanel';
import MediaSelector from './MediaSelector';

// MediaFile interface for media selector
interface MediaFile {
  id: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  width?: number;
  height?: number;
  url: string;
  altText?: string;
  createdAt: string;
  uploadedBy?: {
    id: string;
    name: string;
    photo?: string;
  };
}

interface BlogPostEditorProps {
  isOpen: boolean;
  onClose: () => void;
  post?: BlogPost | null;
  tags: BlogTag[];
  categories: BlogCategory[];
  onSave: (post: BlogPost) => void;
}

export default function BlogPostEditor({
  isOpen,
  onClose,
  post,
  tags,
  categories,
  onSave,
}: BlogPostEditorProps) {
  const [formData, setFormData] = useState<{
    title: string;
    content: string;
    excerpt: string;
    coverImage: string;
    categoryId: string;
    status: 'draft' | 'published';
    seoTitle: string;
    seoDescription: string;
    openGraphImage: string;
    tagIds: string[];
  }>({
    title: '',
    content: '',
    excerpt: '',
    coverImage: '',
    categoryId: '',
    status: 'draft',
    seoTitle: '',
    seoDescription: '',
    openGraphImage: '',
    tagIds: [],
  });

  const [isSaving, setIsSaving] = useState(false);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState<'content' | 'seo' | 'settings'>('content');
  const [showContentSuggestions, setShowContentSuggestions] = useState(false);
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [mediaSelectorTarget, setMediaSelectorTarget] = useState<'coverImage' | 'openGraphImage'>(
    'coverImage'
  );

  // Load post data when editing
  useEffect(() => {
    if (post) {
      setFormData({
        title: post.title || '',
        content: post.content || '',
        excerpt: post.excerpt || '',
        coverImage: post.coverImage || '',
        categoryId: post.category?.id || '',
        status: post.status,
        seoTitle: post.seoTitle || '',
        seoDescription: post.seoDescription || '',
        openGraphImage: post.openGraphImage || '',
        tagIds: post.tags ? post.tags.map((tag) => tag.id) : [],
      });
    } else {
      // Reset form for new post
      setFormData({
        title: '',
        content: '',
        excerpt: '',
        coverImage: '',
        categoryId: '',
        status: 'draft',
        seoTitle: '',
        seoDescription: '',
        openGraphImage: '',
        tagIds: [],
      });
    }
  }, [post, isOpen]);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const saveData: CreateBlogPostData | UpdateBlogPostData = {
        title: formData.title,
        content: formData.content,
        excerpt: formData.excerpt || undefined,
        coverImage: formData.coverImage || undefined,
        categoryId: formData.categoryId || undefined,
        status: formData.status,
        seoTitle: formData.seoTitle || undefined,
        seoDescription: formData.seoDescription || undefined,
        openGraphImage: formData.openGraphImage || undefined,
        tagIds: formData.tagIds,
      };

      let response;
      if (post) {
        // Update existing post
        response = await blogApi.admin.updatePost(post.id, saveData);
      } else {
        // Create new post
        response = await blogApi.admin.createPost(saveData as CreateBlogPostData);
      }

      if (response.success && response.data) {
        onSave(response.data);
        onClose();
      } else {
        console.error('Failed to save post:', response.error);
      }
    } catch (error) {
      console.error('Error saving post:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAIGenerate = async (options: BlogGenerationOptions) => {
    try {
      setIsGenerating(true);
      const response = await blogApi.admin.generateContent(options);

      if (response.success && response.data) {
        // Find the category ID based on the suggested category name
        const suggestedCategory = categories.find(
          (cat) => cat.name.toLowerCase() === response.data.suggestedCategory.toLowerCase()
        );

        setFormData((prev) => ({
          ...prev,
          title: response.data.title,
          content: response.data.content,
          excerpt: response.data.excerpt,
          seoTitle: response.data.seoTitle,
          seoDescription: response.data.seoDescription,
          categoryId: suggestedCategory?.id || '',
          // Map suggested tag names to existing tag IDs
          tagIds: tags
            .filter((tag) =>
              response.data.suggestedTags.some(
                (suggestedTag) =>
                  tag.name.toLowerCase().includes(suggestedTag.toLowerCase()) ||
                  suggestedTag.toLowerCase().includes(tag.name.toLowerCase())
              )
            )
            .map((tag) => tag.id),
        }));
        setShowAIGenerator(false);
      }
    } catch (error) {
      console.error('Error generating content:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleContentSuggestionSelect = async (suggestion: ContentSuggestion) => {
    // Convert the suggestion to BlogGenerationOptions and generate content
    const generationOptions: BlogGenerationOptions = {
      topic: suggestion.topic,
      targetAudience: suggestion.targetAudience,
      keywords: suggestion.keywords,
      tone: suggestion.tone,
      style: suggestion.style,
      wordCount: 800, // Default word count
    };

    await handleAIGenerate(generationOptions);
  };

  const handleOpenMediaSelector = (target: 'coverImage' | 'openGraphImage') => {
    setMediaSelectorTarget(target);
    setShowMediaSelector(true);
  };

  const handleMediaSelect = (mediaFile: MediaFile) => {
    const imageUrl = mediaFile.url;

    if (mediaSelectorTarget === 'coverImage') {
      setFormData((prev) => ({
        ...prev,
        coverImage: imageUrl,
        // Also update OpenGraph image if it's empty
        openGraphImage: prev.openGraphImage || imageUrl,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        openGraphImage: imageUrl,
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {post ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowContentSuggestions(!showContentSuggestions)}
              className="flex items-center space-x-2 px-3 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <Brain className="h-4 w-4" />
              <span>Suggestions</span>
            </button>
            <button
              onClick={() => setShowAIGenerator(!showAIGenerator)}
              className="flex items-center space-x-2 px-3 py-2 text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors"
            >
              <Sparkles className="h-4 w-4" />
              <span>AI Generate</span>
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !formData.title || !formData.content}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{isSaving ? 'Saving...' : 'Save'}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex h-full max-h-[80vh]">
          {/* AI Generator Sidebar */}
          {showAIGenerator && (
            <div className="w-80 border-r border-gray-200 p-6 overflow-y-auto">
              <AIGeneratorPanel onGenerate={handleAIGenerate} isGenerating={isGenerating} />
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('content')}
                className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'content'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Content
              </button>
              <button
                onClick={() => setActiveTab('seo')}
                className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'seo'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                SEO
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'settings'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Settings
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'content' && (
                <div className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter blog post title..."
                    />
                  </div>

                  {/* Excerpt */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt</label>
                    <textarea
                      value={formData.excerpt}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, excerpt: e.target.value }))
                      }
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Brief description of the post..."
                    />
                  </div>

                  {/* Content */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Content *
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, content: e.target.value }))
                      }
                      rows={20}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      placeholder="Write your blog post content in Markdown..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Supports Markdown formatting (# headers, **bold**, *italic*, etc.)
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'seo' && (
                <div className="space-y-6">
                  {/* SEO Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO Title
                    </label>
                    <input
                      type="text"
                      value={formData.seoTitle}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, seoTitle: e.target.value }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="SEO-optimized title (max 60 characters)"
                      maxLength={60}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.seoTitle.length}/60 characters
                    </p>
                  </div>

                  {/* SEO Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO Description
                    </label>
                    <textarea
                      value={formData.seoDescription}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, seoDescription: e.target.value }))
                      }
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Meta description for search engines (max 160 characters)"
                      maxLength={160}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.seoDescription.length}/160 characters
                    </p>
                  </div>

                  {/* Open Graph Image */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Open Graph Image
                    </label>
                    <div className="space-y-3">
                      {formData.openGraphImage && (
                        <div className="relative">
                          <img
                            src={formData.openGraphImage}
                            alt="Open Graph image preview"
                            className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            onClick={() => setFormData((prev) => ({ ...prev, openGraphImage: '' }))}
                            className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleOpenMediaSelector('openGraphImage')}
                          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <ImageIcon className="h-4 w-4" />
                          <span>Select Image</span>
                        </button>
                        <div className="flex-1">
                          <input
                            type="url"
                            value={formData.openGraphImage}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, openGraphImage: e.target.value }))
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Or paste image URL..."
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Recommended size: 1200 × 630 pixels for optimal social media sharing
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="space-y-6">
                  {/* Cover Image */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cover Image
                    </label>
                    <div className="space-y-3">
                      {formData.coverImage && (
                        <div className="relative">
                          <img
                            src={formData.coverImage}
                            alt="Cover image preview"
                            className="w-full h-40 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            onClick={() => setFormData((prev) => ({ ...prev, coverImage: '' }))}
                            className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleOpenMediaSelector('coverImage')}
                          className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <ImageIcon className="h-4 w-4" />
                          <span>Select Image</span>
                        </button>
                        <div className="flex-1">
                          <input
                            type="url"
                            value={formData.coverImage}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, coverImage: e.target.value }))
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Or paste image URL..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={formData.categoryId}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, categoryId: e.target.value }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a category...</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          status: e.target.value as 'draft' | 'published',
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {tags.map((tag) => (
                        <label key={tag.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.tagIds.includes(tag.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData((prev) => ({
                                  ...prev,
                                  tagIds: [...prev.tagIds, tag.id],
                                }));
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  tagIds: prev.tagIds.filter((id) => id !== tag.id),
                                }));
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: tag.color }}
                          />
                          <span className="text-sm text-gray-700">{tag.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Media Selector Modal */}
      <MediaSelector
        isOpen={showMediaSelector}
        onClose={() => setShowMediaSelector(false)}
        onSelect={handleMediaSelect}
        selectedUrl={
          mediaSelectorTarget === 'coverImage' ? formData.coverImage : formData.openGraphImage
        }
        title={
          mediaSelectorTarget === 'coverImage' ? 'Select Cover Image' : 'Select Open Graph Image'
        }
      />

      {/* Content Suggestions Panel */}
      <ContentSuggestionsPanel
        isVisible={showContentSuggestions}
        onSuggestionSelect={handleContentSuggestionSelect}
        onClose={() => setShowContentSuggestions(false)}
      />
    </div>
  );
}

// AI Generator Panel Component
function AIGeneratorPanel({
  onGenerate,
  isGenerating,
}: {
  onGenerate: (options: BlogGenerationOptions) => void;
  isGenerating: boolean;
}) {
  const [aiOptions, setAiOptions] = useState<BlogGenerationOptions>({
    topic: '',
    targetAudience: 'business professionals',
    keywords: [],
    tone: 'professional',
    wordCount: 800,
    includeOutline: false,
    style: 'how-to',
  });

  const [keywordInput, setKeywordInput] = useState('');

  const addKeyword = () => {
    if (keywordInput.trim() && !aiOptions.keywords?.includes(keywordInput.trim())) {
      setAiOptions((prev: BlogGenerationOptions) => ({
        ...prev,
        keywords: [...(prev.keywords || []), keywordInput.trim()],
      }));
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setAiOptions((prev: BlogGenerationOptions) => ({
      ...prev,
      keywords: prev.keywords?.filter((k: string) => k !== keyword) || [],
    }));
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
        <Sparkles className="h-5 w-5 text-purple-600" />
        <span>AI Content Generator</span>
      </h3>

      <div className="space-y-4">
        {/* Topic */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Topic *</label>
          <input
            type="text"
            value={aiOptions.topic}
            onChange={(e) =>
              setAiOptions((prev: BlogGenerationOptions) => ({ ...prev, topic: e.target.value }))
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="e.g., Strategic Planning in Remote Teams"
          />
        </div>

        {/* Target Audience */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
          <input
            type="text"
            value={aiOptions.targetAudience}
            onChange={(e) =>
              setAiOptions((prev: BlogGenerationOptions) => ({
                ...prev,
                targetAudience: e.target.value,
              }))
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="e.g., startup founders, executives"
          />
        </div>

        {/* Tone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
          <select
            value={aiOptions.tone}
            onChange={(e) =>
              setAiOptions((prev: BlogGenerationOptions) => ({
                ...prev,
                tone: e.target.value as any,
              }))
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="professional">Professional</option>
            <option value="casual">Casual</option>
            <option value="inspirational">Inspirational</option>
            <option value="educational">Educational</option>
            <option value="conversational">Conversational</option>
          </select>
        </div>

        {/* Style */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Style</label>
          <select
            value={aiOptions.style}
            onChange={(e) =>
              setAiOptions((prev: BlogGenerationOptions) => ({
                ...prev,
                style: e.target.value as any,
              }))
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="how-to">How-to Guide</option>
            <option value="listicle">Listicle</option>
            <option value="case-study">Case Study</option>
            <option value="opinion">Opinion</option>
            <option value="news">News</option>
            <option value="tutorial">Tutorial</option>
          </select>
        </div>

        {/* Word Count */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Word Count</label>
          <input
            type="number"
            value={aiOptions.wordCount}
            onChange={(e) =>
              setAiOptions((prev: BlogGenerationOptions) => ({
                ...prev,
                wordCount: parseInt(e.target.value) || 800,
              }))
            }
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            min="300"
            max="3000"
            step="100"
          />
        </div>

        {/* Keywords */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
          <div className="flex space-x-2 mb-2">
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Add keyword..."
            />
            <button
              onClick={addKeyword}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {aiOptions.keywords?.map((keyword: string) => (
              <span
                key={keyword}
                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800"
              >
                {keyword}
                <button
                  onClick={() => removeKeyword(keyword)}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Include Outline */}
        <div>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={aiOptions.includeOutline}
              onChange={(e) =>
                setAiOptions((prev: BlogGenerationOptions) => ({
                  ...prev,
                  includeOutline: e.target.checked,
                }))
              }
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">Include content outline</span>
          </label>
        </div>

        {/* Generate Button */}
        <button
          onClick={() => onGenerate(aiOptions)}
          disabled={isGenerating || !aiOptions.topic}
          className="w-full flex items-center justify-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isGenerating ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          <span>{isGenerating ? 'Generating...' : 'Generate Content'}</span>
        </button>
      </div>
    </div>
  );
}
